import React, { useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import { Button, Flex, H2, Input, Line, SelectDropdown } from '../../lib/components'
import { walletAdminServices, opportunityServices } from '../../services'
import { displayDollar, getFileKeyFromSignedUrl } from '../../lib/utils/helpers'
import { showBanner } from '../../global-state/banner-slice'
import { ClientCell } from './client-cell'
import './wallet-admin.scss'

type TopupRow = {
  id: number
  investor_id: number
  investor_name?: string | null
  investor_reference?: string | null
  amount_cents: number
  status: string
  order_id: string
  investment_id?: number | null
  metadata?: Record<string, unknown>
  created_at?: string
}

const readAmountSentCents = (metadata: Record<string, unknown>): number | null =>
  typeof metadata.amountSentCents === 'number' ? metadata.amountSentCents : null

const formatAmountSentCell = (metadata: Record<string, unknown>): string => {
  const amountSent = readAmountSentCents(metadata)
  if (amountSent !== null) {
    return displayDollar(String(amountSent), 'USD', 2, true)
  }
  if (metadata.amountSentExtractionStatus === 'failed') {
    return 'Extraction failed'
  }
  return '—'
}

const initialBankCharges = (metadata: Record<string, unknown>, requestedCents: number): string => {
  const amountSent = readAmountSentCents(metadata)
  if (amountSent === null) return ''
  return String(Math.max(0, amountSent - requestedCents) / 100)
}

const WalletTopupsContainer = () => {
  const dispatch = useDispatch()
  const [status, setStatus] = useState('ORDER_PLACED')
  const [rows, setRows] = useState<TopupRow[]>([])
  const [busyId, setBusyId] = useState<number | null>(null)
  const [approveId, setApproveId] = useState<number | null>(null)
  const [approveAmount, setApproveAmount] = useState('')
  const [approveBankCharges, setApproveBankCharges] = useState('')
  const [rejectId, setRejectId] = useState<number | null>(null)
  const [rejectReason, setRejectReason] = useState('')

  const load = async (nextStatus = status) => {
    const res = await walletAdminServices.searchTopups({
      status: nextStatus || undefined,
      limit: 50,
      offset: 0,
    })
    if (res?.status === 200) setRows(res.data.topups || [])
  }

  useEffect(() => {
    void load()
  }, [])

  const previewSwift = async (paymentCopy: string) => {
    const fileKey = paymentCopy.replace(/\//g, '%2F')
    const signedUrl = getFileKeyFromSignedUrl(fileKey)
    const data = await opportunityServices.previewFile(signedUrl)
    if (data?.data?.url && data.status === 200) {
      window.open(data.data.url, '_blank', 'noopener,noreferrer')
      return
    }
    dispatch(
      showBanner({
        text: (data?.data && data.data.message) || 'Could not open SWIFT copy',
        variant: 'error-banner',
      }),
    )
  }

  const submitApprove = async (row: TopupRow) => {
    const dollars = Number(approveAmount)
    if (!(dollars > 0)) {
      dispatch(
        showBanner({
          text: 'Enter the exact amount received',
          variant: 'error-banner',
        }),
      )
      return
    }
    const bankChargesRaw = approveBankCharges.trim()
    const bankChargesDollars = bankChargesRaw === '' ? 0 : Number(bankChargesRaw)
    if (!Number.isFinite(bankChargesDollars) || bankChargesDollars < 0) {
      dispatch(
        showBanner({
          text: 'Enter a valid bank charges amount',
          variant: 'error-banner',
        }),
      )
      return
    }
    setBusyId(row.id)
    try {
      const payload: {
        status: string
        actualAmountReceived: number
        estimatedChargesCents: number
      } = {
        status: 'APPROVED',
        actualAmountReceived: Math.round(dollars * 100),
        estimatedChargesCents: Math.round(bankChargesDollars * 100),
      }
      const res = await walletAdminServices.updateTopupStatus(row.id, payload)
      if (res?.status !== 200) {
        dispatch(
          showBanner({
            text: res?.data?.message || 'Could not approve top-up',
            variant: 'error-banner',
          }),
        )
        return
      }
      setApproveId(null)
      setApproveAmount('')
      setApproveBankCharges('')
      await load()
    } finally {
      setBusyId(null)
    }
  }

  const submitReject = async (row: TopupRow) => {
    const reason = rejectReason.trim()
    if (!reason) {
      dispatch(
        showBanner({
          text: 'Enter a rejection reason',
          variant: 'error-banner',
        }),
      )
      return
    }
    setBusyId(row.id)
    try {
      const res = await walletAdminServices.updateTopupStatus(row.id, {
        status: 'REJECTED',
        rejectionReason: reason,
      })
      if (res?.status !== 200) {
        dispatch(
          showBanner({
            text: res?.data?.message || 'Could not reject top-up',
            variant: 'error-banner',
          }),
        )
        return
      }
      setRejectId(null)
      setRejectReason('')
      await load()
    } finally {
      setBusyId(null)
    }
  }

  return (
    <Flex variant='column' classes={['wallet-admin']}>
      <H2 text='Wallet top-ups' classes={['mb-24']} />
      <div className='wallet-admin__toolbar'>
        <SelectDropdown
          name='status'
          id='wallet-topup-status'
          formId=''
          value={status}
          list={[
            { text: 'Order placed', value: 'ORDER_PLACED' },
            { text: 'Approved', value: 'APPROVED' },
            { text: 'Rejected', value: 'REJECTED' },
          ]}
          handleChange={(value: string) => {
            setStatus(value)
            void load(value)
          }}
        />
      </div>
      <Line />
      {rows.length === 0 ? (
        <p className='wallet-admin__empty'>No top-ups for this status.</p>
      ) : (
        <div className='w-100 table_scroll_horizontal'>
          <table className='styled-table'>
            <thead>
              <tr>
                <th>Order ID</th>
                <th>Client</th>
                <th>Requested</th>
                <th>Status</th>
                <th>Amount sent</th>
                <th>SWIFT</th>
                <th>Linked invest</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => {
                const metadata = row.metadata || {}
                const paymentCopy =
                  typeof metadata.paymentCopy === 'string' ? metadata.paymentCopy : ''
                const actual =
                  typeof metadata.actualAmountReceived === 'number'
                    ? metadata.actualAmountReceived
                    : null
                const openActions = row.status === 'ORDER_PLACED' || row.status === 'INITIATED'

                return (
                  <tr key={row.id}>
                    <td>{row.order_id}</td>
                    <td>
                      <ClientCell name={row.investor_name} reference={row.investor_reference} />
                    </td>
                    <td>
                      {displayDollar(String(row.amount_cents), 'USD', 2, true)}
                      {actual !== null ? (
                        <>
                          <br />
                          <small>Credited {displayDollar(String(actual), 'USD', 2, true)}</small>
                        </>
                      ) : null}
                    </td>
                    <td>{row.status === 'PAYMENT_RECEIVED' ? 'APPROVED' : row.status}</td>
                    <td>{formatAmountSentCell(metadata)}</td>
                    <td>
                      {paymentCopy ? (
                        <button
                          type='button'
                          className='wallet-admin__link'
                          onClick={() => void previewSwift(paymentCopy)}
                        >
                          View SWIFT
                        </button>
                      ) : (
                        '—'
                      )}
                    </td>
                    <td>{row.investment_id || '—'}</td>
                    <td>
                      {openActions ? (
                        <div className='wallet-admin__actions wallet-admin__actions--stack'>
                          {approveId === row.id ? (
                            <>
                              <small>
                                Requested {displayDollar(String(row.amount_cents), 'USD', 2, true)}
                              </small>
                              <small>Amount sent {formatAmountSentCell(metadata)}</small>
                              <Input
                                type='number'
                                name={`approve-${row.id}`}
                                labelText='Amount received'
                                placeholder='Amount received'
                                value={approveAmount}
                                handleChange={(e) => setApproveAmount(e.target.value)}
                                classes={['wallet-admin__no-spinner']}
                              />
                              <Input
                                type='number'
                                name={`approve-charges-${row.id}`}
                                labelText='Bank charges'
                                placeholder='Bank charges'
                                value={approveBankCharges}
                                handleChange={(e) => setApproveBankCharges(e.target.value)}
                                classes={['wallet-admin__no-spinner']}
                              />
                              <Button
                                text='Confirm approve'
                                variant='primary-btn'
                                isDisabled={busyId === row.id}
                                handleClick={() => void submitApprove(row)}
                              />
                              <Button
                                text='Cancel'
                                variant='secondary-btn'
                                isDisabled={busyId === row.id}
                                handleClick={() => {
                                  setApproveId(null)
                                  setApproveAmount('')
                                  setApproveBankCharges('')
                                }}
                              />
                            </>
                          ) : rejectId === row.id ? (
                            <>
                              <Input
                                type='text'
                                name={`reject-${row.id}`}
                                labelText='Rejection reason'
                                placeholder='Reason'
                                value={rejectReason}
                                handleChange={(e) => setRejectReason(e.target.value)}
                              />
                              <Button
                                text='Confirm reject'
                                variant='primary-btn'
                                isDisabled={busyId === row.id}
                                handleClick={() => void submitReject(row)}
                              />
                              <Button
                                text='Cancel'
                                variant='secondary-btn'
                                isDisabled={busyId === row.id}
                                handleClick={() => {
                                  setRejectId(null)
                                  setRejectReason('')
                                }}
                              />
                            </>
                          ) : (
                            <>
                              <Button
                                text='Approve'
                                variant='primary-btn'
                                isDisabled={busyId === row.id}
                                handleClick={() => {
                                  setRejectId(null)
                                  setApproveId(row.id)
                                  setApproveAmount(String(row.amount_cents / 100))
                                  setApproveBankCharges(
                                    initialBankCharges(metadata, row.amount_cents),
                                  )
                                }}
                              />
                              <Button
                                text='Reject'
                                variant='secondary-btn'
                                isDisabled={busyId === row.id}
                                handleClick={() => {
                                  setApproveId(null)
                                  setRejectId(row.id)
                                  setRejectReason('')
                                }}
                              />
                            </>
                          )}
                        </div>
                      ) : typeof metadata.rejectionReason === 'string' ? (
                        <small>{metadata.rejectionReason}</small>
                      ) : typeof metadata.estimatedChargesCents === 'number' ? (
                        <small>
                          Charges{' '}
                          {displayDollar(String(metadata.estimatedChargesCents), 'USD', 2, true)}
                        </small>
                      ) : (
                        '—'
                      )}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
    </Flex>
  )
}

export { WalletTopupsContainer }
